import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import MobilePageHeader from '../MobilePageHeader';
import PageHeader from '../PageHeader';
import Hidden from '@material-ui/core/Hidden';
import clsx from 'clsx';
import SkeletonPage from '../SkeletonPage';

export const PageBase = styled.div`
  width: 100%;
  margin: 0 auto;

  @media (min-width: ${props => props.theme.breakpoints.values.md}px) {
    &:not(.full-width) {
      max-width: ${props => props.theme.pageWidth}px;
    }
  }
`;

export const PageWrapper = styled.div`
  &:not(.disable-padding) {
    padding: 16px;
  }

  @media (max-width: ${props => props.theme.breakpoints.values.md}px) {
    &.has-primary {
      padding-bottom: ${props => props.theme.spacing(4) + 56}px;
    }
  }
`;

const Page = ({
  children,
  title,
  titleHidden,
  breadcrumbs,
  fullWidth,
  linkComponent,
  primaryAction,
  secondaryActions,
  subNavigation,
  mobilePageHeaderProps,
  disablePadding,
  useBreadcrumbNavigation,
  isLoading,
  style,
}) => {
  if (isLoading) {
    return (
      <SkeletonPage
        title={title}
        subNavigation={subNavigation?.tabs?.length}
        fullWidth={fullWidth}
        breadcrumbs={breadcrumbs?.length}
        primaryAction={primaryAction}
        style={style}
      >
        {children}
      </SkeletonPage>
    );
  }

  return (
    <PageBase className={clsx({ 'full-width': fullWidth })} style={style}>
      <Hidden implementation="js" mdUp>
        <MobilePageHeader
          title={title}
          titleHidden={titleHidden}
          breadcrumbs={breadcrumbs}
          linkComponent={linkComponent}
          primaryAction={primaryAction}
          subNavigation={subNavigation}
          useBreadcrumbNavigation={useBreadcrumbNavigation}
          secondaryActions={secondaryActions}
          {...mobilePageHeaderProps}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <PageHeader
          title={title}
          titleHidden={titleHidden}
          breadcrumbs={breadcrumbs}
          linkComponent={linkComponent}
          primaryAction={primaryAction}
          subNavigation={subNavigation}
          secondaryActions={secondaryActions}
        />
      </Hidden>
      <PageWrapper
        className={clsx({ 'disable-padding': disablePadding, 'has-primary': !!primaryAction })}
      >
        {children}
      </PageWrapper>
    </PageBase>
  );
};

const breadcrumbShape = PropTypes.shape({
  label: PropTypes.string,
  path: PropTypes.string,
  active: PropTypes.bool,
});

const secondaryActionShape = PropTypes.shape({
  text: PropTypes.string,
  action: PropTypes.func,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
});

const subNavigationShape = PropTypes.shape({
  label: PropTypes.string,
  path: PropTypes.string,
});

const defaultPrimaryActionShape = {
  text: PropTypes.string,
  action: PropTypes.func,
  disabled: PropTypes.bool,
  icon: PropTypes.array,
};

Page.propTypes = {
  children: PropTypes.node,
  mobilePageHeaderProps: PropTypes.object,
  title: PropTypes.string,
  titleHidden: PropTypes.bool,
  breadcrumbs: PropTypes.arrayOf(breadcrumbShape),
  fullWidth: PropTypes.bool,
  linkComponent: PropTypes.oneOfType([
    PropTypes.element,
    // eslint-disable-next-line react/no-typos
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.string,
  ]),
  primaryAction: PropTypes.shape({
    ...defaultPrimaryActionShape,
    actions: PropTypes.arrayOf(PropTypes.shape({ ...defaultPrimaryActionShape })),
  }),
  secondaryActions: PropTypes.arrayOf(secondaryActionShape),
  subNavigation: PropTypes.shape({
    current: PropTypes.string,
    tabs: PropTypes.arrayOf(subNavigationShape),
  }),
  disablePadding: PropTypes.bool,
  useBreadcrumbNavigation: PropTypes.bool,
  style: PropTypes.object,
  isLoading: PropTypes.bool,
};

Page.defaultProps = {
  children: undefined,
  mobilePageHeaderProps: undefined,
  title: '',
  titleHidden: false,
  breadcrumbs: undefined,
  fullWidth: false,
  linkComponent: 'a',
  primaryAction: undefined,
  secondaryActions: undefined,
  subNavigation: undefined,
  disablePadding: false,
  useBreadcrumbNavigation: true,
  style: undefined,
  isLoading: false,
};

export default Page;
